import { Pencil, Plus, Wrench } from "lucide-react";
import { useState } from "react";

import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorBanner,
  Input,
  Modal,
  Select,
  Spinner,
  Switch,
} from "@/components/ui";
import { toApiError } from "@/lib/api";
import { useFormat, useT } from "@/lib/i18n";

import { useServiceCrud, useServices } from "@/lib/queries";
import type { Service } from "@/lib/types";

const EMPTY = {
  name: "",
  // A esmagadora maioria é serviço; orçamento é a escolha consciente.
  kind: "service",
  duration_minutes: 30,
  price: "",
  buffer_after_minutes: 5,
  buffer_before_minutes: 0,
  online_bookable: true,
  // Quase tudo numa oficina precisa do carro; o contrário é a exceção.
  requires_vehicle: true,
};

export default function Services() {
  const t = useT();
  const { money, duration } = useFormat();
  const { data: services, isLoading } = useServices();
  const [editing, setEditing] = useState<Service | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <div className="p-4 lg:p-8">
      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold lg:text-2xl">{t("nav.services")}</h1>
          <p className="text-sm text-steel-muted">{t("services.subtitle")}</p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus size={18} />
          {t("common.add")}
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-8" />
        </div>
      ) : services?.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Wrench size={40} />}
            title={t("services.emptyTitle")}
            description={t("services.emptyText")}
            action={
              <Button onClick={() => setCreating(true)}>{t("services.addOne")}</Button>
            }
          />
        </Card>
      ) : (
        <div className="space-y-2">
          {services?.map((s) => (
            <Card key={s.id} className="flex items-center gap-3 p-4">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{s.name}</span>
                  {!s.is_active && (
                    <Badge className="bg-carbon-700 text-steel-dim">{t("common.inactive")}</Badge>
                  )}
                  {!s.online_bookable && (
                    <Badge className="border-amber-700/50 bg-amber-950/50 text-amber-300">
                      {t("services.offSite")}
                    </Badge>
                  )}
                  {s.kind === "quote" && (
                    <Badge className="border-sky-700/60 bg-sky-950/50 text-sky-300">
                      {t("services.quote")}
                    </Badge>
                  )}
                </div>
                <p className="mt-0.5 text-sm text-steel-muted">
                  {duration(s.duration_minutes)}
                  {s.total_block_minutes !== s.duration_minutes && (
                    <span className="text-steel-dim">
                      {" "}
                      {t("services.bufferSuffix", {
                        n: s.total_block_minutes - s.duration_minutes,
                      })}
                    </span>
                  )}
                </p>
              </div>
              {/* "R$ 0,00" numa lista de preços lê-se como serviço de
                  graça, não como "esta visita é para orçar". */}
              <span className="font-semibold">
                {s.kind === "quote" ? (
                  <span className="text-sm font-normal text-steel-muted">
                    {t("services.noCharge")}
                  </span>
                ) : (
                  money(s.price)
                )}
              </span>
              <Button variant="ghost" size="sm" onClick={() => setEditing(s)}>
                <Pencil size={16} />
              </Button>
            </Card>
          ))}
        </div>
      )}

      <ServiceForm
        open={creating || editing !== null}
        service={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
      />
    </div>
  );
}

function ServiceForm({
  open,
  service,
  onClose,
}: {
  open: boolean;
  service: Service | null;
  onClose: () => void;
}) {
  const t = useT();
  const { currency } = useFormat();
  const simbolo = currency === "BRL" ? "R$" : "$";
  const { create, update, remove } = useServiceCrud();
  const [form, setForm] = useState<Record<string, unknown>>(EMPTY);
  const [loaded, setLoaded] = useState<number | null>(null);

  // Carrega os valores do serviço em edição uma única vez por abertura.
  if (open && service && loaded !== service.id) {
    setForm({
      name: service.name,
      kind: service.kind,
      duration_minutes: service.duration_minutes,
      price: service.price,
      buffer_before_minutes: service.buffer_before_minutes,
      buffer_after_minutes: service.buffer_after_minutes,
      online_bookable: service.online_bookable,
      requires_vehicle: service.requires_vehicle,
    });
    setLoaded(service.id);
  }
  if (open && !service && loaded !== null) {
    setForm(EMPTY);
    setLoaded(null);
  }

  const orcamento = form.kind === "quote";
  const mutation = service ? update : create;
  const error = mutation.isError ? toApiError(mutation.error) : null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = service ? { id: service.id, ...form } : form;
    mutation.mutate(payload as never, {
      onSuccess: () => {
        setLoaded(null);
        onClose();
      },
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={service ? t("services.edit") : t("services.new")}
    >
      <form onSubmit={submit} className="space-y-4">
        <Input
          label={t("common.name")}
          required
          value={form.name as string}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <Select
          label={t("services.kind")}
          value={form.kind as string}
          onChange={(e) => setForm({ ...form, kind: e.target.value })}
        >
          <option value="service">{t("services.kindService")}</option>
          <option value="quote">{t("services.kindQuote")}</option>
        </Select>
        <p className="-mt-2 text-xs text-steel-dim">
          {orcamento ? t("services.kindQuoteHint") : t("services.kindServiceHint")}
        </p>

        {/* Uma coluna no orçamento: sem o preço ao lado, a duração
            sozinha com metade da largura parece campo faltando. */}
        <div className={orcamento ? "grid gap-3" : "grid grid-cols-2 gap-3"}>
          <Input
            label={t("services.duration")}
            type="number"
            min={5}
            step={5}
            required
            value={form.duration_minutes as number}
            onChange={(e) =>
              setForm({ ...form, duration_minutes: Number(e.target.value) })
            }
          />
          {/* O campo some no orçamento em vez de aparecer travado em zero:
              campo desabilitado é convite a perguntar "por que não posso
              digitar aqui?", e a resposta já está no seletor acima. */}
          {!orcamento && (
            <Input
              label={t("services.price", { moeda: simbolo })}
              type="number"
              min={0}
              step="0.01"
              required
              value={form.price as string}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
            />
          )}
        </div>

        <div className="rounded-[4px] bg-carbon-900 p-4">
          <p className="mb-1 text-sm font-medium text-steel">{t("services.buffers")}</p>
          <p className="mb-3 text-xs text-steel-muted">{t("services.buffersHint")}</p>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label={t("services.bufferBefore")}
              type="number"
              min={0}
              step={5}
              value={form.buffer_before_minutes as number}
              onChange={(e) =>
                setForm({ ...form, buffer_before_minutes: Number(e.target.value) })
              }
            />
            <Input
              label={t("services.bufferAfter")}
              type="number"
              min={0}
              step={5}
              value={form.buffer_after_minutes as number}
              onChange={(e) =>
                setForm({ ...form, buffer_after_minutes: Number(e.target.value) })
              }
            />
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="size-4 accent-[var(--color-nitro-500)]"
            checked={form.online_bookable as boolean}
            onChange={(e) => setForm({ ...form, online_bookable: e.target.checked })}
          />
          {t("services.onlineBookable")}
        </label>

        <Switch
          checked={form.requires_vehicle as boolean}
          onChange={(v) => setForm({ ...form, requires_vehicle: v })}
          label={t("services.requiresVehicle")}
          hint={t("services.requiresVehicleHint")}
        />

        <ErrorBanner message={error?.message} />

        <div className="flex gap-2">
          {service && (
            <Button
              type="button"
              variant="danger"
              onClick={() =>
                remove.mutate(service.id, {
                  onSuccess: () => {
                    setLoaded(null);
                    onClose();
                  },
                })
              }
            >
              {t("services.deactivate")}
            </Button>
          )}
          <Button type="submit" className="flex-1" disabled={mutation.isPending}>
            {mutation.isPending ? (
              <Spinner className="size-4 border-carbon-900/30 border-t-carbon-900" />
            ) : (
              t("common.save")
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
